export const handleCropMouseDown = (
	e: React.MouseEvent,
	containerRef: React.RefObject<HTMLDivElement | null>,
	setIsDragging: (value: boolean) => void,
	setDragStart: (value: { x: number; y: number }) => void,
	cropArea: { x: number; y: number; width: number; height: number },
) => {
	e.preventDefault();
	e.stopPropagation();

	const containerRect = containerRef.current?.getBoundingClientRect();
	if (!containerRect) return;

	setIsDragging(true);
	setDragStart({
		x: e.clientX - cropArea.x,
		y: e.clientY - cropArea.y,
	});
};

export const handleResizeMouseDown = (
	e: React.MouseEvent,
	handle: string,
	setIsResizing: (value: boolean) => void,
	setResizeHandle: (value: string) => void,
	setInitialCropArea: (value: {
		x: number;
		y: number;
		width: number;
		height: number;
	}) => void,
	setDragStart: (value: { x: number; y: number }) => void,
	cropArea: { x: number; y: number; width: number; height: number },
) => {
	e.preventDefault();
	e.stopPropagation();

	setIsResizing(true);
	setResizeHandle(handle);
	setInitialCropArea(cropArea);
	setDragStart({
		x: e.clientX,
		y: e.clientY,
	});
};

export const handleMouseMove = (
	e: MouseEvent,
	isDragging: boolean,
	isResizing: boolean,
	containerRef: React.RefObject<HTMLDivElement | null>,
	dragStart: { x: number; y: number },
	cropArea: { x: number; y: number; width: number; height: number },
	resizeHandle: string,
	initialCropArea: { x: number; y: number; width: number; height: number },
	setCropArea: React.Dispatch<
		React.SetStateAction<{
			x: number;
			y: number;
			width: number;
			height: number;
		}>
	>,
) => {
	if (isDragging) {
		const containerRect = containerRef.current?.getBoundingClientRect();
		if (!containerRect) return;

		const newX = e.clientX - dragStart.x;
		const newY = e.clientY - dragStart.y;

		const maxX = 350 - cropArea.width;
		const maxY = 350 - cropArea.height;

		setCropArea((prev) => ({
			...prev,
			x: Math.max(0, Math.min(maxX, newX)),
			y: Math.max(0, Math.min(maxY, newY)),
		}));
	} else if (isResizing) {
		const deltaX = e.clientX - dragStart.x;
		const deltaY = e.clientY - dragStart.y;

		setCropArea((prev) => {
			const newCrop = { ...prev };

			switch (resizeHandle) {
				case 'nw': // Top-left
					newCrop.x = Math.max(0, initialCropArea.x + deltaX);
					newCrop.y = Math.max(0, initialCropArea.y + deltaY);
					newCrop.width = Math.max(50, initialCropArea.width - deltaX);
					newCrop.height = Math.max(50, initialCropArea.height - deltaY);
					break;
				case 'ne': // Top-right
					newCrop.y = Math.max(0, initialCropArea.y + deltaY);
					newCrop.width = Math.max(50, initialCropArea.width + deltaX);
					newCrop.height = Math.max(50, initialCropArea.height - deltaY);
					break;
				case 'sw': // Bottom-left
					newCrop.x = Math.max(0, initialCropArea.x + deltaX);
					newCrop.width = Math.max(50, initialCropArea.width - deltaX);
					newCrop.height = Math.max(50, initialCropArea.height + deltaY);
					break;
				case 'se': // Bottom-right
					newCrop.width = Math.max(50, initialCropArea.width + deltaX);
					newCrop.height = Math.max(50, initialCropArea.height + deltaY);
					break;
				case 'n': // Top edge
					newCrop.y = Math.max(0, initialCropArea.y + deltaY);
					newCrop.height = Math.max(50, initialCropArea.height - deltaY);
					break;
				case 's': // Bottom edge
					newCrop.height = Math.max(50, initialCropArea.height + deltaY);
					break;
				case 'w': // Left edge
					newCrop.x = Math.max(0, initialCropArea.x + deltaX);
					newCrop.width = Math.max(50, initialCropArea.width - deltaX);
					break;
				case 'e': // Right edge
					newCrop.width = Math.max(50, initialCropArea.width + deltaX);
					break;
			}

			if (newCrop.x + newCrop.width > 350) {
				newCrop.width = 350 - newCrop.x;
			}
			if (newCrop.y + newCrop.height > 350) {
				newCrop.height = 350 - newCrop.y;
			}

			return newCrop;
		});
	}
};

export const handleCrop = (
	canvasRef: React.RefObject<HTMLCanvasElement | null>,
	imageRef: React.RefObject<HTMLImageElement | null>,
	cropArea: { x: number; y: number; width: number; height: number },
	onCropComplete: (croppedImageUrl: string) => void,
	onClose: () => void,
) => {
	const canvas = canvasRef.current;
	const image = imageRef.current;

	if (!canvas || !image) return;

	const ctx = canvas.getContext('2d');
	if (!ctx) return;

	canvas.width = cropArea.width;
	canvas.height = cropArea.height;

	const imageRect = image.getBoundingClientRect();
	const scaleX = image.naturalWidth / imageRect.width;
	const scaleY = image.naturalHeight / imageRect.height;

	const sourceX = cropArea.x * scaleX;
	const sourceY = cropArea.y * scaleY;
	const sourceWidth = cropArea.width * scaleX;
	const sourceHeight = cropArea.height * scaleY;

	ctx.drawImage(
		image,
		sourceX,
		sourceY,
		sourceWidth,
		sourceHeight,
		0,
		0,
		cropArea.width,
		cropArea.height,
	);

	canvas.toBlob(
		(blob) => {
			if (blob) {
				const croppedUrl = URL.createObjectURL(blob);
				onCropComplete(croppedUrl);
				onClose();
			}
		},
		'image/jpeg',
		0.9,
	);
};
